import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCommentCable } from './hooks/useCommentCable';
import { formatDistanceToNow } from 'date-fns';

interface Comment {
  id: number;
  body: string;
  user_id: number;
  user: {
    id: number;
    name: string;
  };
  created_at: string;
}

interface TypingUser {
  id: number;
  name: string;
}

interface CommentsProps {
  commentableType: string;
  commentableId: string | number;
}

const Comments: React.FC<CommentsProps> = ({ commentableType, commentableId }) => {
  const [newComment, setNewComment] = useState('');
  const queryClient = useQueryClient();
  const { notifyTyping } = useCommentCable({ commentableType, commentableId });

  // Query for comments
  const { data: comments = [] } = useQuery({
    queryKey: ['comments', commentableType, commentableId],
    queryFn: async () => {
      const response = await axios.get(`/api/comments`, {
        params: {
          commentable_type: commentableType,
          commentable_id: commentableId,
        },
      });
      return response.data;
    },
  });

  // Query for typing users
  const { data: typingUsers = [] } = useQuery({
    queryKey: ['typing_users', commentableType, commentableId],
    queryFn: () => [],
    staleTime: Infinity,
  });

  // Query for commentable details
  const { data: commentableDetails } = useQuery({
    queryKey: ['commentable_details', commentableType, commentableId],
    queryFn: async () => {
      if (commentableType === 'UsaSpendingRecipient') {
        const response = await axios.get(`/api/usa_spending_recipients/${commentableId}`);
        return response.data;
      } else if (commentableType === 'UsaSpendingAward') {
        const response = await axios.get(`/api/commentable/${commentableId}`);
        return response.data;
      }
      return null;
    },
  });

  // Mutation for creating comments
  const createCommentMutation = useMutation({
    mutationFn: async (body: string) => {
      const response = await axios.post('/api/comments', {
        commentable_type: commentableType,
        commentable_id: commentableId,
        comment: { body },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', commentableType, commentableId] });
      setNewComment('');
    },
  });

  // Mutation for deleting comments
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: number) => {
      const response = await axios.delete(`/api/comments/${commentId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', commentableType, commentableId] });
    },
  });

  const handleDelete = (commentId: number) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newComment.trim()) return;
    createCommentMutation.mutate(newComment);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewComment(e.target.value);
    notifyTyping();
  };

  const formatCommentableType = (type: string) => {
    return type.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Commentable Details */}
      {false && commentableDetails && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {formatCommentableType(commentableType)}
          </div>
          <div className="font-medium text-gray-900 dark:text-white">
            {commentableDetails.name || JSON.stringify(commentableDetails)}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {comments.map((comment: Comment) => (
          <div key={comment.id} className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white">
                  {comment.user?.name || 'Anonymous'}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
              </div>
              <button
                onClick={() => handleDelete(comment.id)}
                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Delete comment"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{comment.body}</p>
          </div>
        ))}
      </div>

      {/* Typing Indicators */}
      {typingUsers.length > 0 && (
        <div className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
          {typingUsers.length === 1
            ? `${typingUsers[0].name} is typing...`
            : `${typingUsers.length} people are typing...`}
        </div>
      )}

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
        <textarea
          value={newComment}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Type comment"
          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
            bg-white dark:bg-gray-700
            border-gray-300 dark:border-gray-600
            text-gray-800 dark:text-gray-200
            placeholder-gray-500 dark:placeholder-gray-400
            min-h-[100px]"
        />
        <div className="mt-2 flex justify-between items-center">
          <button
            type="submit"
            disabled={createCommentMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
              focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {createCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Comments;
